import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { analytics } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let polls = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          analytics.subscriptionStarted();
          await refreshUser();
          setStatus("paid");
          toast("Welcome. Your membership is active.");
          setTimeout(() => navigate("/begin", { replace: true }), 1600);
          return;
        }
        if (data.status === "failed" || data.payment_status === "failed") {
          setStatus("error");
          return;
        }
      } catch (_) {
        // keep polling; transient
      }
      polls += 1;
      if (polls >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params, navigate, refreshUser]);

  return (
    <div
      data-testid="payment-success-screen"
      className="min-h-screen flex flex-col items-center justify-center px-8 text-center"
    >
      {status === "checking" && (
        <>
          <span className="thinking-dot text-[var(--mirror-text-3)] text-sm tracking-[0.3em] uppercase">
            confirming
          </span>
          <p className="mt-6 text-[var(--mirror-text-3)] text-sm">Confirming your membership…</p>
        </>
      )}

      {status === "paid" && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col items-center"
        >
          <div className="h-14 w-14 rounded-full border border-[var(--mirror-accent)] flex items-center justify-center">
            <Check size={22} className="text-[var(--mirror-accent)]" />
          </div>
          <h1 className="mt-8 font-serif-display font-light text-3xl sm:text-4xl tracking-tight">
            You're a member.
          </h1>
          <p className="mt-4 text-[var(--mirror-text-3)] text-sm">Taking you back to Mirror…</p>
        </motion.div>
      )}

      {(status === "timeout" || status === "error") && (
        <div className="flex flex-col items-center">
          <h1 className="font-serif-display font-light text-2xl sm:text-3xl tracking-tight">
            {status === "timeout" ? "Still processing…" : "Something went wrong."}
          </h1>
          <p className="mt-4 text-[var(--mirror-text-3)] text-sm max-w-sm">
            {status === "timeout"
              ? "Your payment is being confirmed. It may take a moment — check Settings shortly."
              : "We couldn't confirm the payment. If you were charged, it will reflect soon."}
          </p>
          <button
            data-testid="payment-success-continue"
            onClick={() => navigate("/begin", { replace: true })}
            className="mt-8 px-8 py-3 rounded-full border border-[var(--mirror-border-strong)] text-sm tracking-[0.2em] uppercase hover:bg-[var(--mirror-surface)] transition-colors duration-700"
          >
            Continue
          </button>
        </div>
      )}
    </div>
  );
}
